import { Building2, Landmark, Globe, Scale, ArrowRightLeft, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

export default function ExpertiseSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  const services = [
    {
      icon: (
        <div className="w-16 h-16 rounded-full bg-white border border-[#dfe3e8] flex items-center justify-center">
          <Building2 className="w-7 h-7 text-[#1a1a1a]" strokeWidth={1} />
        </div>
      ),
      title: "Finance bancaire",
      shortDescription: "Accompagnement complet dans vos relations bancaires et montage de dossiers de financement adaptés à vos besoins.",
      prestations: [
        "Entrée en relation avec la banque et les salles de marché",
        "Négociation des tarifs bancaires",
        "Montage des dossiers de crédit et financement en dinars et en devises",
        "Gestion de la trésorerie",
        "Conformité réglementaire et audit",
        "Structuration de financements et analyse de crédit"
      ],
      advantages: [
        "Accompagnement personnalisé",
        "Optimisation de vos coûts de financement",
        "Sécurisation de vos opérations bancaires"
      ]
    },
    {
      icon: (
        <div className="w-16 h-16 rounded-full bg-white border border-[#dfe3e8] flex items-center justify-center">
          <Landmark className="w-7 h-7 text-[#1a1a1a]" strokeWidth={1} />
        </div>
      ),
      title: "Finance islamique",
      shortDescription: "Expertise en instruments financiers conformes à la charia et structuration de produits islamiques innovants.",
      prestations: [
        "Conseil sur les instruments financiers conformes à la charia",
        "Structuration de produits islamiques (Mourabaha, Moucharaka, Ijara, Sukuk...)",
        "Développement de partenariats avec des institutions spécialisées",
        "Audit de conformité charaique",
        "Formation et sensibilisation",
        "Conseils stratégiques en finance islamique"
      ],
      advantages: [
        "Respect des principes éthiques islamiques",
        "Diversification de vos sources de financement",
        "Accès à un marché en forte croissance"
      ]
    },
    {
      icon: (
        <div className="w-16 h-16 rounded-full bg-white border border-[#dfe3e8] flex items-center justify-center">
          <Globe className="w-7 h-7 text-[#1a1a1a]" strokeWidth={1} />
        </div>
      ),
      title: "Paiements internationaux & Incoterms",
      shortDescription: "Maîtrise des opérations de commerce extérieur, crédits documentaires et gestion des risques de change.",
      prestations: [
        "Crédit documentaire et lettre de crédit",
        "Encaissement documentaire et remise documentaire",
        "Virement et transferts internationaux",
        "Réglementation des changes et gestion des risques de change",
        "Choix et maîtrise des Incoterms"
      ],
      advantages: [
        "Sécurisation de vos transactions internationales",
        "Optimisation de vos coûts logistiques",
        "Réduction des risques de change",
        "Conformité aux réglementations internationales"
      ]
    },
    {
      icon: (
        <div className="w-16 h-16 rounded-full bg-white border border-[#dfe3e8] flex items-center justify-center">
          <ArrowRightLeft className="w-7 h-7 text-[#1a1a1a]" strokeWidth={1} />
        </div>
      ),
      title: "Transferts internationaux & réglementation tunisienne",
      shortDescription: "Expertise en transferts de fonds internationaux et conformité avec la réglementation tunisienne des changes.",
      prestations: [
        "Ouvertures des comptes en devises et en dinars convertibles",
        "Transferts internationaux à caractère personnel (études à l'étranger, économie sur salaires)",
        "Transferts liés aux services rendus en Tunisie et à l'étranger",
        "Transfert de capitaux (investissement, dividendes, gestion et suivi des fiches d'investissement)",
        "Fiche d'investissement et conformité réglementaire",
        "Réglementation des changes tunisienne et conformité aux normes internationales"
      ],
      advantages: [
        "Accompagnement personnalisé",
        "Obtention des autorisations",
        "Conformité réglementaire"
      ]
    },
    {
      icon: (
        <div className="w-16 h-16 rounded-full bg-white border border-[#dfe3e8] flex items-center justify-center">
          <Scale className="w-7 h-7 text-[#1a1a1a]" strokeWidth={1} />
        </div>
      ),
      title: "Arbitrage et médiation",
      shortDescription: "Résolution de litiges bancaires, commerciaux et financiers par des solutions d'arbitrage et de médiation professionnelles.",
      prestations: [
        "Arbitrage bancaire et financier",
        "Médiation conventionnelle et commerciale",
        "Solutions aux conflits commerciaux et contractuels",
        "Litiges liés aux projets d'investissement et partenariats internationaux"
      ],
      advantages: [
        "Résolution rapide des conflits",
        "Confidentialité garantie et préservation des relations",
        "Économie de temps et d'argent"
      ]
    },
  ];

  return (
    <section
      id="expertise"
      className="scroll-mt-20 bg-white text-[#1a1a1a] py-0 px-0 overflow-hidden w-full"
    >

      {/* Services grid - Responsive with 15px top padding */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 w-full pt-[15px]">
        {services.map((service, i) => (
          <div
            key={i}
            className="relative bg-white border-t border-r border-b border-[#dfe3e8]
                       flex flex-col items-start justify-between p-8 sm:p-10 md:p-12
                       transition-all duration-500 ease-in-out
                       group overflow-hidden cursor-pointer
                       sm:last:border-r xl:last:border-r-0
                       hover:shadow-xl hover:-translate-y-2"
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{ minHeight: "380px" }}
          >
            <div>
              <div className="mb-6 md:mb-9">{service.icon}</div>
              <h3 className="font-normal text-[20px] sm:text-[22px] md:text-[24px] mb-4 md:mb-6 leading-[1.3] text-[#1a1a1a]">
                {service.title}
              </h3>
              <p className="text-[#666666] text-[14px] sm:text-[15px] leading-[1.75]">
                {service.shortDescription}
              </p>
            </div>
            
            <div className={`mt-6 md:mt-8 transition-all duration-300 ${hoveredCard === i ? "opacity-100 visible" : "opacity-0 invisible"}`}>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedService(service);
                }}
                className="inline-flex items-center gap-2.5 text-[#1a1a1a] font-medium text-[10px] sm:text-[11px] 
                           uppercase tracking-[0.2em] transition-all duration-300
                           border-b border-[#1a1a1a] pb-1.5
                           group-hover:gap-4"
              >
                En savoir plus
                <svg className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-1" 
                     fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Enhanced Modal - Separate Borders for Left (Red) and Right (Blue) */}
      {selectedService && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
             onClick={() => setSelectedService(null)}>
          <div className="relative w-full max-w-4xl mx-auto"
               onClick={(e) => e.stopPropagation()}>
            
            {/* Close Button */}
            <button onClick={() => setSelectedService(null)}
                    className="absolute -top-3 -right-3 z-30 
                             w-10 h-10 rounded-full 
                             bg-white hover:bg-gray-50 shadow-2xl
                             flex items-center justify-center transition-all duration-300
                             text-gray-600 hover:text-gray-900 hover:scale-110 group">
              <X className="w-5 h-5 transition-transform group-hover:rotate-90 duration-300" />
            </button>

            {/* Modal Content - Scrollable Container with Separate Borders */}
            <div className="flex flex-col lg:flex-row gap-4 max-h-[85vh] overflow-hidden">
                
              {/* Left - Pure White Background (60%) with Red Border */}
              <div className="w-full lg:w-[60%] bg-white shadow-2xl overflow-y-auto rounded-2xl border-2 border-[#B91C1C]">
                <div className="p-6 space-y-6">
                  {/* Prestations */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1 h-6 bg-gradient-to-b from-[#0B2545] to-[#134074] rounded-full"></div>
                      <h3 className="text-xl font-light text-[#0B2545]">
                        Nos prestations
                      </h3>
                    </div>
                    <ul className="space-y-3">
                      {selectedService.prestations.map((prestation, idx) => (
                        <li key={idx} className="flex items-start gap-3 group">
                          <div className="mt-1 flex-shrink-0">
                            <div className="w-5 h-5 rounded-full bg-[#0B2545]/10 flex items-center justify-center
                                          group-hover:bg-[#0B2545] transition-colors duration-300">
                              <svg className="w-3 h-3 text-[#0B2545] group-hover:text-white transition-colors" 
                                   fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                          <span className="text-[13px] text-[#666] leading-[1.7] group-hover:text-[#0B2545] transition-colors">
                            {prestation}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Divider */}
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-xs text-gray-400">•••</span>
                    </div>
                  </div>

                  {/* Avantages */}
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1 h-6 bg-gradient-to-b from-[#B91C1C] to-[#991b1b] rounded-full"></div>
                      <h4 className="text-lg font-light text-[#0B2545]">
                        Avantages clés
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      {selectedService.advantages.map((advantage, idx) => (
                        <div key={idx} className="flex items-center gap-2.5 p-2.5 rounded-lg
                                                  hover:bg-[#F7F9FB] transition-all duration-300 group">
                          <div className="w-2 h-2 rounded-full bg-gradient-to-r from-[#B91C1C] to-[#991b1b] 
                                        flex-shrink-0 group-hover:scale-125 transition-transform"></div>
                          <span className="text-[13px] text-[#666] group-hover:text-[#0B2545] 
                                         font-medium transition-colors">
                            {advantage}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Gradient Background (40%) with Blue Border */}
              <div className="w-full lg:w-[40%] relative overflow-hidden backdrop-blur-2xl shadow-2xl rounded-2xl border-2 border-[#0B2545]"
                   style={{
                     background: 'linear-gradient(to bottom right, rgba(240, 240, 245, 0.95), rgba(255, 255, 255, 0.98))'
                   }}>
                
                {/* Content - Centered */}
                <div className="relative z-10 p-6 h-full flex flex-col items-center justify-center text-center min-h-[400px]">
                  <div className="space-y-5 max-w-xs">
                    {/* Icon */}
                    <div className="flex justify-center">
                      <div className="w-20 h-20 rounded-full bg-white backdrop-blur-sm 
                                    border-2 border-[#0B2545]/10 flex items-center justify-center
                                    shadow-xl transform hover:scale-110 transition-all duration-300">
                        <div className="scale-[1.3]">
                          {selectedService.icon}
                        </div>
                      </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-light text-[#0B2545] leading-tight px-2">
                      {selectedService.title}
                    </h2>

                    {/* Description */}
                    <p className="text-sm text-[#1a1a1a]/80 leading-relaxed px-2">
                      {selectedService.shortDescription}
                    </p>

                    {/* CTA Button */}
                    <div className="pt-3">
                      <Link to="/contact" onClick={() => setSelectedService(null)}
                            className="inline-flex items-center gap-2 
                                     bg-[#0B2545] hover:bg-[#0a1f3a] text-white 
                                     font-medium text-[13px] px-6 py-3 rounded-full 
                                     transition-all duration-300 hover:shadow-2xl
                                     hover:scale-105 group">
                        <span>Contactez-nous</span>
                        <svg className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" 
                             fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </section>
  );
}